import { useQuery } from '@tanstack/react-query';
import UserManagement from '../../views/UserManagement';
import { useApiClients } from '../../client/api/useApiClient';
import {
  GetAllRolesResponse,
  GetAllDepartmentResponse,
  GetAllUsersResponse,
  Department,
  Roles,
  UserControllerGetUsersStatusEnum,
} from '@EHSNavigator/backend';
import { useSearchParams } from 'react-router';
import Loader from '../../components/Loader';
import { UserWithOrgContext } from '../../types/user';
import { useLocalStorage } from '@uidotdev/usehooks';

export default function UserManagementPage() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const currentPage = searchParams.get('page') ?? 1;
  const department = searchParams.get('department') ?? '';
  const role = searchParams.get('role') ?? '';
  const limit = Number(searchParams.get('limit')) || 10;
  const status = searchParams.get('status') ?? 'all';

  const { UserApi, DepartmentApi, RolesApi } = useApiClients();
  const [userInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const organizationId = userInfo?.orgContext?.org.id || undefined;
  const organizationOwnerId = userInfo?.orgContext?.id || undefined;

  const {
    data: allUsers,
    isLoading: isUsersLoading,
    isRefetching,
  } = useQuery<GetAllUsersResponse>({
    queryKey: [
      'allUsers',
      searchQuery,
      Number(currentPage),
      department,
      role,
      status,
      Number(limit),
      organizationId,
      organizationOwnerId,
    ],
    queryFn: async () => {
      const response = await UserApi.userControllerGetUsers({
        page: Number(currentPage) || 1,
        limit: Number(limit),
        search: searchQuery,
        departmentId: department,
        roleId: role,
        status: status as UserControllerGetUsersStatusEnum,
        ...(organizationId ? { organizationId } : {}),
        ...(organizationOwnerId ? { userId: organizationOwnerId } : {}),
      });
      return response?.data;
    },
  });
  const { data: allDepartments, isLoading: departmetnsLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['departments'],
      queryFn: async () => {
        const response =
          await DepartmentApi.departmentControllerFindAllDepartment({
            disablePagination: true,
            ...(organizationId ? { organizationId } : {}),
          });
        return response?.data;
      },
    });
  const { data: allRoles, isLoading: rolesLoading } =
    useQuery<GetAllRolesResponse>({
      queryKey: ['roles'],
      queryFn: async () => {
        const response = await RolesApi.rolesControllerGetAllRoles();
        return response?.data;
      },
    });
  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const roles =
    allRoles?.roles?.map((node: Roles) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  if (rolesLoading || departmetnsLoading) {
    return <Loader />;
  }

  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <UserManagement
        data={allUsers as GetAllUsersResponse}
        roles={roles}
        departments={departments}
        isLoading={isUsersLoading || isRefetching}
        params={{
          search: searchQuery,
          page: Number(currentPage),
          department,
          role,
          status,
          limit: Number(limit),
        }}
      />
    </div>
  );
}
